import type { UserRole } from '@micarahr/shared-types';
import { PERMISSIONS, ROLE_PERMISSIONS, permissionsForRoles, type Permission } from './index';

/**
 * Web authorization.
 *
 * The mobile app deliberately honours a narrow slice of the permission map. The
 * HR & Administration web app honours all of it, so it needs its own resolver —
 * calling `can()` from the mobile module here would silently strip every HR
 * permission and make the whole application look like an employee's phone.
 *
 * The web check is also *scoped*: a department head may hold `employee.view_all`
 * within their own department and nothing outside it. Scope is enforced in the
 * service layer, not by hiding navigation (§6).
 */

export interface WebSubject {
  userId: string;
  roles: UserRole[];
  /** Permissions granted by custom roles, on top of the platform roles. */
  extraPermissions?: string[];
  /** Empty means company-wide. Otherwise the departments this user administers. */
  scopedDepartmentIds?: string[];
  /** The employee record this account belongs to, when it has one. */
  employeeId?: string | null;
}

/** Every permission the subject holds in the web application. */
export function webPermissionsFor(subject: WebSubject): Set<string> {
  const granted = new Set<string>(permissionsForRoles(subject.roles));
  for (const extra of subject.extraPermissions ?? []) granted.add(extra);
  return granted;
}

export function webCan(subject: WebSubject, permission: string): boolean {
  return webPermissionsFor(subject).has(permission);
}

export function webCanAny(subject: WebSubject, permissions: string[]): boolean {
  if (permissions.length === 0) return true;
  const granted = webPermissionsFor(subject);
  return permissions.some((permission) => granted.has(permission));
}

export function webCanAll(subject: WebSubject, permissions: string[]): boolean {
  const granted = webPermissionsFor(subject);
  return permissions.every((permission) => granted.has(permission));
}

/**
 * Department scoping.
 *
 * `true` means the subject may see records belonging to that department. An
 * unscoped subject (an HR administrator) sees everything; a scoped one (a
 * department head) does not, no matter which permissions they hold.
 */
export function withinScope(subject: WebSubject, departmentId: string | null): boolean {
  const scope = subject.scopedDepartmentIds ?? [];
  if (scope.length === 0) return true;
  if (departmentId === null) return false;
  return scope.includes(departmentId);
}

/* -------------------------------------------------------------------------- */
/* Permission catalogue for the role editor                                    */
/* -------------------------------------------------------------------------- */

export interface PermissionGroup {
  key: string;
  label: string;
  description: string;
  permissions: { key: string; label: string; description: string; sensitive?: boolean }[];
}

/**
 * The permission catalogue, grouped for the role editor.
 *
 * The spec is explicit that a giant read-only matrix is the wrong shape (§40) —
 * an administrator needs to understand what they are granting, so each
 * permission carries a sentence and the dangerous ones are marked.
 */
export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    key: 'employees',
    label: 'Employees',
    description: 'Employee records, personal information and employment history.',
    permissions: [
      {
        key: PERMISSIONS.EMPLOYEE_VIEW_ALL,
        label: 'View all employees',
        description: 'See the full employee directory and every employee profile.',
      },
      {
        key: PERMISSIONS.EMPLOYEE_MANAGE,
        label: 'Manage employees',
        description: 'Create employees, edit records, change employment status and transfer.',
        sensitive: true,
      },
    ],
  },
  {
    key: 'organization',
    label: 'Organisation',
    description: 'Departments, teams, positions, job levels and work locations.',
    permissions: [
      {
        key: PERMISSIONS.ORGANIZATION_MANAGE,
        label: 'Manage organisation structure',
        description: 'Create and change departments, teams, positions and reporting lines.',
        sensitive: true,
      },
      {
        key: PERMISSIONS.WORK_LOCATION_MANAGE,
        label: 'Manage work locations',
        description: 'Create and edit offices, branches and field sites.',
      },
      {
        key: PERMISSIONS.GEOFENCE_MANAGE,
        label: 'Manage geofences',
        description: 'Change the attendance boundary of a work location.',
        sensitive: true,
      },
    ],
  },
  {
    key: 'attendance',
    label: 'Attendance',
    description: 'Attendance monitoring, evidence review and corrections.',
    permissions: [
      {
        key: PERMISSIONS.TEAM_ATTENDANCE_VIEW,
        label: 'View team attendance',
        description: 'See attendance for the teams this user manages.',
      },
      {
        key: PERMISSIONS.ATTENDANCE_REVIEW,
        label: 'Review attendance',
        description: 'Open attendance evidence and decide on flagged records.',
      },
      {
        key: PERMISSIONS.ATTENDANCE_CORRECTION_APPROVE,
        label: 'Approve attendance corrections',
        description: 'Approve or reject correction requests, creating an adjustment.',
      },
      {
        key: PERMISSIONS.ATTENDANCE_POLICY_MANAGE,
        label: 'Manage attendance policy',
        description: 'Change grace periods, geofence rules and evidence requirements.',
        sensitive: true,
      },
    ],
  },
  {
    key: 'scheduling',
    label: 'Scheduling',
    description: 'Shift templates, schedule assignment and publication.',
    permissions: [
      {
        key: PERMISSIONS.SHIFT_MANAGE,
        label: 'Manage shifts',
        description: 'Create and edit shift templates and schedule groups.',
      },
      {
        key: PERMISSIONS.SCHEDULE_ASSIGN,
        label: 'Assign schedules',
        description: 'Assign and publish schedules for employees and teams.',
      },
    ],
  },
  {
    key: 'leave',
    label: 'Leave',
    description: 'Leave types, balances and approvals.',
    permissions: [
      {
        key: PERMISSIONS.LEAVE_APPROVE_HR,
        label: 'Approve leave (HR)',
        description: 'Decide on leave at the HR step of the approval chain.',
      },
      {
        key: PERMISSIONS.LEAVE_BALANCE_MANAGE,
        label: 'Manage leave balances',
        description: 'Adjust balances. Every adjustment requires a reason and is audited.',
        sensitive: true,
      },
      {
        key: PERMISSIONS.LEAVE_TYPE_MANAGE,
        label: 'Manage leave types',
        description: 'Create and configure leave types and their policies.',
      },
    ],
  },
  {
    key: 'overtime',
    label: 'Overtime',
    description: 'Overtime approval and payroll readiness.',
    permissions: [
      {
        key: PERMISSIONS.OVERTIME_APPROVE_HR,
        label: 'Approve overtime (HR)',
        description: 'Decide on overtime and adjust approved hours.',
      },
    ],
  },
  {
    key: 'payroll',
    label: 'Payroll',
    description: 'Payroll runs, calculations, approval and payslip publication.',
    permissions: [
      {
        key: PERMISSIONS.PAYROLL_RUN,
        label: 'Run payroll',
        description: 'Create periods, import attendance and calculate a preview.',
        sensitive: true,
      },
      {
        key: PERMISSIONS.PAYROLL_APPROVE,
        label: 'Approve payroll',
        description: 'Approve and lock a payroll run.',
        sensitive: true,
      },
      {
        key: PERMISSIONS.PAYSLIP_PUBLISH,
        label: 'Publish payslips',
        description: 'Release payslips to employees and revoke a publication.',
        sensitive: true,
      },
      {
        key: PERMISSIONS.STATUTORY_TABLE_MANAGE,
        label: 'Manage statutory tables',
        description: 'Edit contribution and tax configuration versions.',
        sensitive: true,
      },
    ],
  },
  {
    key: 'compensation',
    label: 'Compensation',
    description: 'Salary records and effective-dated changes.',
    permissions: [
      {
        key: 'compensation.view',
        label: 'View compensation',
        description: 'See salary, allowances and compensation history.',
        sensitive: true,
      },
      {
        key: 'compensation.manage',
        label: 'Manage compensation',
        description: 'Record salary changes with an effective date and reason.',
        sensitive: true,
      },
    ],
  },
  {
    key: 'benefits',
    label: 'Benefits and loans',
    description: 'Benefit plans, enrollments and payroll-deduction records.',
    permissions: [
      { key: 'benefits.view', label: 'View benefits', description: 'See plans and enrollments.' },
      {
        key: 'benefits.manage',
        label: 'Manage benefits',
        description: 'Create plans and enroll or remove employees.',
      },
      { key: 'loans.view', label: 'View loans', description: 'See loan records and balances.' },
      {
        key: 'loans.manage',
        label: 'Manage loans',
        description: 'Record loans and deduction schedules.',
        sensitive: true,
      },
    ],
  },
  {
    key: 'recruitment',
    label: 'Recruitment',
    description: 'Requisitions, candidates, interviews and offers.',
    permissions: [
      {
        key: PERMISSIONS.RECRUITMENT_MANAGE,
        label: 'Manage recruitment',
        description: 'Create requisitions, move candidates and issue offers.',
      },
    ],
  },
  {
    key: 'onboarding',
    label: 'Onboarding and probation',
    description: 'New-hire onboarding cases and probation decisions.',
    permissions: [
      {
        key: 'onboarding.manage',
        label: 'Manage onboarding',
        description: 'Create cases, assign tasks and close onboarding.',
      },
      {
        key: 'probation.decide',
        label: 'Decide probation',
        description: 'Record the HR decision on a probation review.',
        sensitive: true,
      },
    ],
  },
  {
    key: 'performance',
    label: 'Performance and learning',
    description: 'Review cycles, ratings, courses and training records.',
    permissions: [
      {
        key: 'performance.manage',
        label: 'Manage performance',
        description: 'Run review cycles and finalise ratings.',
      },
      {
        key: PERMISSIONS.LEARNING_MANAGE,
        label: 'Manage learning',
        description: 'Create courses, schedule sessions and record completions.',
      },
    ],
  },
  {
    key: 'documents',
    label: 'Documents and policies',
    description: 'Employee documents, company policies and announcements.',
    permissions: [
      {
        key: PERMISSIONS.DOCUMENT_MANAGE,
        label: 'Manage documents',
        description: 'Upload, verify, replace and archive employee documents.',
      },
      {
        key: PERMISSIONS.POLICY_PUBLISH,
        label: 'Publish policies',
        description: 'Publish a policy version and request acknowledgement.',
      },
      {
        key: PERMISSIONS.ANNOUNCEMENT_PUBLISH,
        label: 'Publish announcements',
        description: 'Create, schedule and publish company announcements.',
      },
    ],
  },
  {
    key: 'assets',
    label: 'Assets',
    description: 'Company asset inventory and assignment.',
    permissions: [
      {
        key: PERMISSIONS.ASSET_MANAGE,
        label: 'Manage assets',
        description: 'Register, assign, transfer and retire assets.',
      },
    ],
  },
  {
    key: 'employee_relations',
    label: 'Employee relations',
    description: 'Concerns, incidents and disciplinary records.',
    permissions: [
      {
        key: 'employee_relations.view',
        label: 'View cases',
        description: 'Open employee-relations cases. Highly confidential.',
        sensitive: true,
      },
      /*
       * The keys the server enforces, not a coarse "manage". There was one —
       * `employee_relations.manage` — which no route checked and the server's
       * catalogue did not know: ticking it failed the role save, and gating
       * "Add entry" on it hid the button from everybody.
       */
      {
        key: 'employee_relations.view_confidential',
        label: 'Read case substance',
        description: 'Read confidential notes and statements, not only that a case exists.',
        sensitive: true,
      },
      {
        key: 'employee_relations.create',
        label: 'Open cases',
        description: 'Open a new employee-relations case.',
        sensitive: true,
      },
      {
        key: 'employee_relations.edit',
        label: 'Record case progress',
        description: 'Add notes, notices, meetings, explanations and decisions to a case.',
        sensitive: true,
      },
    ],
  },
  {
    key: 'offboarding',
    label: 'Offboarding',
    description: 'Separation cases, clearance and account revocation.',
    permissions: [
      {
        key: 'offboarding.manage',
        label: 'Manage offboarding',
        description: 'Start cases, assign clearance and close them.',
        sensitive: true,
      },
    ],
  },
  {
    key: 'reports',
    label: 'Reports and audit',
    description: 'Reporting and the immutable audit log.',
    permissions: [
      {
        key: PERMISSIONS.REPORT_VIEW,
        label: 'View reports',
        description: 'Open the reporting workspace and export results.',
      },
      {
        key: PERMISSIONS.AUDIT_LOG_VIEW,
        label: 'View audit log',
        description: 'Read the audit log. Entries can never be edited or deleted.',
        sensitive: true,
      },
    ],
  },
  {
    key: 'settings',
    label: 'Administration',
    description: 'System users, roles and company configuration.',
    permissions: [
      {
        key: PERMISSIONS.SYSTEM_ADMINISTER,
        label: 'Administer the system',
        description: 'Manage users, roles and every company setting.',
        sensitive: true,
      },
    ],
  },
];

/** Flat list of every permission key the role editor can grant. */
export const ALL_WEB_PERMISSION_KEYS: string[] = PERMISSION_GROUPS.flatMap((group) =>
  group.permissions.map((permission) => permission.key),
);

/**
 * Extra permissions granted by role that are not in the platform `PERMISSIONS`
 * map — compensation, benefits, cases and so on. Declared here so the seeded
 * roles and the role editor agree.
 */
export const WEB_ROLE_EXTRAS: Partial<Record<UserRole, string[]>> = {
  hr_officer: ['onboarding.manage', 'benefits.view', 'loans.view'],
  hr_administrator: [
    'onboarding.manage',
    'probation.decide',
    'performance.manage',
    'benefits.view',
    'benefits.manage',
    'loans.view',
    'employee_relations.view',
    'employee_relations.create',
    'employee_relations.edit',
    'offboarding.manage',
    'compensation.view',
  ],
  payroll_officer: ['compensation.view', 'benefits.view', 'loans.view'],
  payroll_administrator: [
    'compensation.view',
    'compensation.manage',
    'benefits.view',
    'benefits.manage',
    'loans.view',
    'loans.manage',
  ],
  company_administrator: ALL_WEB_PERMISSION_KEYS,
  super_administrator: ALL_WEB_PERMISSION_KEYS,
  auditor: ['compensation.view'],
  learning_administrator: ['performance.manage'],
  manager: ['onboarding.manage'],
  department_head: ['onboarding.manage', 'performance.manage'],
};

/** Every permission a platform role carries in the web application. */
export function webPermissionsForRole(role: UserRole): string[] {
  return [
    ...new Set<string>([...(ROLE_PERMISSIONS[role] ?? []), ...(WEB_ROLE_EXTRAS[role] ?? [])]),
  ];
}

/** Roles that may sign in to the HR & Administration web app at all. */
export const WEB_SUPPORTED_ROLES: UserRole[] = [
  'super_administrator',
  'company_administrator',
  'hr_administrator',
  'hr_officer',
  'payroll_administrator',
  'payroll_officer',
  'recruiter',
  'department_head',
  'manager',
  'team_leader',
  'auditor',
  'asset_custodian',
  'learning_administrator',
];

const ROLE_LABELS: Record<UserRole, string> = {
  super_administrator: 'Super Administrator',
  company_administrator: 'Company Administrator',
  hr_administrator: 'HR Administrator',
  hr_officer: 'HR Officer',
  payroll_administrator: 'Payroll Administrator',
  payroll_officer: 'Payroll Officer',
  recruiter: 'Recruiter',
  department_head: 'Department Head',
  manager: 'Manager',
  team_leader: 'Team Leader',
  employee: 'Employee',
  auditor: 'Auditor',
  asset_custodian: 'Asset Custodian',
  learning_administrator: 'Learning Administrator',
};

export function roleLabel(role: UserRole): string {
  return ROLE_LABELS[role] ?? role;
}

/** The role shown as the workspace context — the highest-privilege one held. */
export function primaryWebRole(roles: UserRole[]): UserRole {
  const order: UserRole[] = [
    'super_administrator',
    'company_administrator',
    'hr_administrator',
    'payroll_administrator',
    'hr_officer',
    'payroll_officer',
    'recruiter',
    'learning_administrator',
    'asset_custodian',
    'auditor',
    'department_head',
    'manager',
    'team_leader',
    'employee',
  ];
  return order.find((role) => roles.includes(role)) ?? 'employee';
}

export type { Permission };

export { permissionLabel } from './labels';
